import React, { useState, useEffect } from "react";
import { GetEtkOrders } from "../../services/OrderService";
import EtkOrdersReportTable from "./EtkOrdersReportTable";
import CommonLoader from "../common/CommonLoader";
import { NotificationManager } from "react-notifications";
import { TextField, Button, Grid, Tooltip } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { generateEtkOrdersPdf } from "../../../common-utilities/etkOrdersPdfExport";

const BASE_URL = "https://scanner.envirolabsinc.com";

const EtkOrdersReport = () => {
    const [loading, setLoading] = useState(false);
    const [ordersData, setOrdersData] = useState(null);
    const [ordersCount, setOrdersCount] = useState(0);
    const [searchCriteria, setSearchCriteria] = useState({
        startDate: "",
        endDate: "",
        searchText: "",
        pageNumber: 1,
        pageSize: 25,
    });

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        const today = new Date();
        const startDateValue = new Date(today);
        startDateValue.setDate(today.getDate() - 30);

        const startDateStr = startDateValue.toISOString().split("T")[0];
        const endDateStr = today.toISOString().split("T")[0];

        setStartDate(startDateStr);
        setEndDate(endDateStr);

        const initialCriteria = {
            startDate: startDateStr,
            endDate: endDateStr,
            searchText: "",
            pageNumber: 1,
            pageSize: 25,
        };
        setSearchCriteria(initialCriteria);
        getEtkOrders(1, 25, startDateStr, endDateStr, "");
    }, []);

    const getEtkOrders = async (pageNumber, pageSize, fromDate, toDate, searchTextValue) => {
        try {
            setLoading(true);
            let response = await GetEtkOrders(
                fromDate ?? startDate,
                toDate ?? endDate,
                pageNumber,
                pageSize,
                searchTextValue ?? searchText
            );
            setOrdersData(response.results);
            setOrdersCount(response.count);
        } catch (err) {
            NotificationManager.error("Error getting ETK orders. " + err, "Error");
        } finally {
            setLoading(false);
        }
    };

    const handleSearchClick = () => {
        if (startDate === "" || endDate === "") {
            NotificationManager.error("Invalid Dates");
            return;
        }
        const updatedSearchCriteria = {
            ...searchCriteria,
            startDate,
            endDate,
            searchText,
            pageNumber: 1,
        };
        setSearchCriteria(updatedSearchCriteria);
        getEtkOrders(1, searchCriteria.pageSize, startDate, endDate, searchText);
    };

    const handleDateKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearchClick();
        }
    };

    const onChangePageSettings = async (pageNumber, pageSize) => {
        const updatedSearchCriteria = {
            ...searchCriteria,
            pageNumber,
            pageSize,
        };
        setSearchCriteria(updatedSearchCriteria);
        getEtkOrders(pageNumber, pageSize, startDate, endDate, searchText);
    };

    const handleSaveReport = () => {
        if (!ordersData || ordersData.length === 0) {
            NotificationManager.warning("No data available to export.");
            return;
        }
        generateEtkOrdersPdf({
            ordersData,
            startDate,
            endDate,
            baseUrl: BASE_URL,
        });
    };

    return (
        <div className="vlp-component-container">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <span className="title">ETK Orders Report</span>
            </div>
            <hr />
            <div className="columns">
                <div className="column">
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <Tooltip title="TypeProject Description / Test Kit and press Enter to search" arrow placement="top">
                                <TextField
                                    label="Project Description / Test Kit"
                                    type="text"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    placeholder="Search by test kit name..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={handleDateKeyDown}
                                />
                            </Tooltip>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Tooltip title="Press Enter to search" arrow placement="top">
                                <TextField
                                    label="From Date"
                                    type="date"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    onKeyDown={handleDateKeyDown}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Tooltip title="Press Enter to search" arrow placement="top">
                                <TextField
                                    label="To Date"
                                    type="date"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    onKeyDown={handleDateKeyDown}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="medium"
                                onClick={handleSearchClick}
                                style={{ height: "40px" }}
                            >
                                Search
                            </Button>
                        </Grid>
                        {ordersData && ordersData.length > 0 && (
                            <Grid item xs={12} sm={2}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="medium"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleSaveReport}
                                    style={{ height: "40px", whiteSpace: "nowrap" }}
                                >
                                    Save Report
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </div>
            </div>
            {ordersData !== null && (
                <EtkOrdersReportTable
                    ordersData={ordersData}
                    count={ordersCount}
                    onChange={onChangePageSettings}
                    baseUrl={BASE_URL}
                />
            )}
            <CommonLoader loading={loading} />
        </div>
    );
};

export default EtkOrdersReport;